import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';
import { createPortal } from 'react-dom';
import PhoneInput from '../components/PhoneInput';

const FILTER_MODES = {
  ALL: 'all',
  POPULAR: 'popular',
};

const defaultContact = { name: '', whatsapp: '', email: '' };
const WHATSAPP_TRUNK_COUNTRIES = new Set(['+44', '+234', '+233', '+254']);

const normalizeWhatsAppNumber = (value) => {
  const raw = (value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\+\d{1,3})(0+)(\d+)$/);
  if (!match) return raw;
  const country = match[1];
  if (!WHATSAPP_TRUNK_COUNTRIES.has(country)) return raw;
  return `${country}${match[3]}`;
};

const FIELD_LABELS = {
  customerName: 'Name',
  customerWhatsApp: 'WhatsApp',
  customerEmail: 'Email',
  contact: 'Contact',
  deliveryOption: 'Delivery option',
  pickupSlot: 'Pickup slot',
  items: 'Items',
  note: 'Notes',
};

const formatFieldLabel = (field) => {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  const itemMatch = field.match(/^items\\[(\\d+)\\]\\.([\\w-]+)$/);
  if (itemMatch) {
    const index = Number(itemMatch[1]) + 1;
    const suffix = FIELD_LABELS[itemMatch[2]] || itemMatch[2];
    return `Item ${index} ${suffix}`;
  }
  return field;
};

export default function CustomerPreorderPage() {
  const navigate = useNavigate();
  const currencyCode = useCurrencyCode();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterMode, setFilterMode] = useState(FILTER_MODES.ALL);
  const [cart, setCart] = useState([]);
  const [deliveryOption, setDeliveryOption] = useState('delivery');
  const [note, setNote] = useState('');
  const [contact, setContact] = useState(defaultContact);
  const [address, setAddress] = useState('');
  const [rememberDetails, setRememberDetails] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [drawerMode, setDrawerMode] = useState('closed');
  const STORAGE_KEY = 'customer-preorder-persist';

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/products/public-preorders');
      setProducts(res.data || []);
    } catch (err) {
      console.error('Failed to load preorder catalog', err);
      toast.error('Unable to load preorder items right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage?.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.cart)) {
        setCart(parsed.cart);
      }
      if (parsed.note) setNote(parsed.note);
      if (parsed.deliveryOption) setDeliveryOption(parsed.deliveryOption);
      const rememberFlag =
        typeof parsed.rememberDetails === 'boolean' ? parsed.rememberDetails : true;
      setRememberDetails(rememberFlag);
      if (rememberFlag && parsed.contact) {
        setContact(parsed.contact);
      }
      if (rememberFlag && parsed.address) {
        setAddress(parsed.address);
      }
    } catch (err) {
      console.error('Failed to hydrate customer preorder storage', err);
    }
  }, []);

  useEffect(() => {
    try {
      const payload = {
        cart,
        note,
        deliveryOption,
        rememberDetails,
      };
      if (rememberDetails) {
        payload.contact = contact;
        payload.address = address;
      }
      window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      console.error('Failed to persist customer preorder state', err);
    }
  }, [cart, note, deliveryOption, rememberDetails, contact, address]);

  const filteredProducts = useMemo(() => {
    const base =
      filterMode === FILTER_MODES.POPULAR
        ? products.filter((product) => product.isPopular)
        : products;
    const categoryFiltered =
      selectedCategory === 'all'
        ? base
        : base.filter(
            (product) =>
              (product.category || 'Uncategorized').toLowerCase() ===
              selectedCategory.toLowerCase()
          );
    if (!searchTerm.trim()) return categoryFiltered;
    const term = searchTerm.trim().toLowerCase();
    return categoryFiltered.filter((product) =>
      [product.name, product.sku, product.category, product.woocommerceSlug]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [filterMode, products, searchTerm, selectedCategory]);

  const categoryList = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const bucket = (product.category || 'Uncategorized').trim();
      if (!bucket) return;
      const key = bucket.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { name: bucket, count: 0 });
      }
      map.get(key).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleAddToCart = (product) => {
    setSuccessMessage('');
    setCart((prev) => {
      const existing = prev.find((item) => item.productId === product.id);
      if (existing) {
        return prev.map((item) =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [
        ...prev,
        {
          productId: product.id,
          name: product.name,
          price: Number(product.priceInclTax ?? product.price ?? 0),
          quantity: 1,
        },
      ];
    });
  };

  const handleQuantityChange = (productId, nextQuantity) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.productId === productId
            ? { ...item, quantity: Math.max(1, nextQuantity) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const handleRemoveItem = (productId) => {
    setCart((prev) => prev.filter((item) => item.productId !== productId));
  };

  const resetForm = () => {
    setCart([]);
    setDeliveryOption('delivery');
    setNote('');
    setContact(defaultContact);
    setAddress('');
  };

  const handleSubmit = async () => {
    const normalizedWhatsApp = normalizeWhatsAppNumber(contact.whatsapp);
    if (normalizedWhatsApp && normalizedWhatsApp !== contact.whatsapp) {
      setContact((prev) => ({ ...prev, whatsapp: normalizedWhatsApp }));
    }
    if (!contact.name.trim() || !normalizedWhatsApp.trim()) {
      toast.error('Please provide your name and WhatsApp number.');
      return;
    }
    if (cart.length === 0) {
      toast.error('Add at least one product to your cart.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post('/preorders', {
        customerName: contact.name.trim(),
        customerWhatsApp: normalizedWhatsApp.trim(),
        customerEmail: contact.email.trim() || null,
        note: note.trim() || null,
        deliveryOption,
        source: 'customer',
        products: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
        customerAddress: address.trim() || null,
      });
      const preorder = response?.data || {};
      const confirmationId = preorder?.id;
      setSuccessMessage(
        'Thanks! Your request has been submitted. We will confirm availability shortly.'
      );
      toast.success('Pre-order sent.');
      resetForm();
      if (confirmationId) {
        navigate(`/preorders/confirmation/${confirmationId}`, {
          state: {
            preorder: {
              id: preorder.id,
              publicRef: preorder.publicRef,
              createdAt: preorder.createdAt,
              deliveryOption: preorder.deliveryOption,
              pickupSlot: preorder.pickupSlot || preorder?.metadata?.pickupSlot || null,
              totalAmount: preorder.totalAmount,
              grandTotal: preorder.grandTotal || preorder?.metadata?.grandTotal || null,
              items: preorder.items || [],
            },
          },
        });
      }
    } catch (err) {
      console.error('Failed to submit customer preorder', err);
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 429) {
        const retryAfter = data?.retryAfterSeconds;
        const message = data?.message || 'Too many requests. Please try again shortly.';
        toast.error(
          retryAfter ? `${message} Try again in ${retryAfter}s.` : message
        );
        return;
      }
      if (data?.error === 'validation_error') {
        const message = data?.message || 'Please check the details and try again.';
        const fields = data?.fields && typeof data.fields === 'object' ? data.fields : null;
        if (fields) {
          const entries = Object.entries(fields);
          if (entries.length > 0) {
            const lines = entries.slice(0, 5).map(([field, reason]) =>
              `• ${formatFieldLabel(field)}: ${reason}`
            );
            if (entries.length > 5) {
              lines.push(`• +${entries.length - 5} more`);
            }
            toast.error(`${message}\n${lines.join('\n')}`);
            return;
          }
        }
        toast.error(message);
        return;
      }
      toast.error(data?.message || 'Unable to submit your pre-order.');
    } finally {
      setSubmitting(false);
    }
  };

  const [clearModalOpen, setClearModalOpen] = useState(false);

  const clearCart = () => {
    if (cart.length === 0) return;
    setClearModalOpen(true);
  };

  const confirmClearCart = () => {
    setCart([]);
    setClearModalOpen(false);
  };

  const isDrawerVisible = drawerMode !== 'closed';
  const isCartMode = drawerMode === 'cart';
  const isCheckoutMode = drawerMode === 'checkout';

  const openCart = () => setDrawerMode('cart');
  const openCheckout = () => setDrawerMode('checkout');
  const closeDrawer = () => setDrawerMode('closed');
  const backToCart = () => setDrawerMode('cart');

  const filterButtonBase =
    'rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] transition';

  const cartSummaryLabel = `${totalItems} ${totalItems === 1 ? 'item' : 'items'}`;
  const cartBadgeLabel = `Cart ${totalItems}`;
  const visibleLabel = filterMode === FILTER_MODES.POPULAR ? 'Popular' : 'Menu';
  const drawerPanelBase =
    'fixed inset-y-0 right-0 flex w-full max-w-md flex-col overflow-hidden rounded-l-[30px] border border-slate-800/70 bg-slate-900/95 p-5 shadow-[0_60px_120px_-40px_rgba(0,0,0,0.9)] backdrop-blur-xl transition duration-300 sm:mx-4';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isDrawerVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerVisible]);

  const portalTarget =
    typeof document !== 'undefined' && document.body ? document.body : null;

  const clearCartDialog = (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60">
      <div className="mx-4 w-full max-w-sm rounded-3xl border border-slate-800 bg-slate-900/95 p-5 text-center shadow-[0_20px_60px_-30px_rgba(0,0,0,0.9)]">
        <p className="text-sm font-semibold text-white">Clear cart?</p>
        <p className="mt-2 text-xs text-slate-400">
          This will remove all items from your cart. Continue?
        </p>
        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={() => setClearModalOpen(false)}
            className="flex-1 rounded-2xl border border-slate-700/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 transition hover:border-emerald-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmClearCart}
            className="flex-1 rounded-2xl border border-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-rose-200 transition hover:border-rose-400"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 pb-24 pt-8 text-white">
      {clearModalOpen && portalTarget && createPortal(clearCartDialog, portalTarget)}
      <main className="mx-auto flex max-w-5xl flex-col gap-6">
        <header className="space-y-3">
          <div>
            <p className="text-xs uppercase tracking-[0.5em] text-slate-500">Ofoodi Local</p>
            <h1 className="text-3xl font-semibold leading-snug sm:text-4xl">Place a pre-order</h1>
            <p className="text-sm text-slate-400">
              Tap the favourites you love, add a note, and we’ll confirm availability via WhatsApp or email.
            </p>
          </div>
          {successMessage && (
            <div className="rounded-2xl border border-emerald-500/60 bg-emerald-900/30 px-4 py-3 text-sm text-emerald-200">
              {successMessage}
            </div>
          )}
        </header>

        <section className="space-y-3 rounded-[34px] border border-slate-800/70 bg-slate-900/70 p-5 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)] backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Search & filters</p>
            <button
              type="button"
              onClick={() => setFiltersExpanded((prev) => !prev)}
              className="text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-300 transition hover:text-emerald-200"
            >
              {filtersExpanded ? 'Hide filters' : 'Filters'}
            </button>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search the menu..."
              className="flex-1 rounded-2xl border border-slate-800 bg-slate-950/50 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="rounded-full border border-slate-700/80 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 transition hover:border-emerald-400"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={openCart}
                className="rounded-full border border-emerald-500/60 bg-emerald-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-200 transition hover:bg-emerald-500/20"
              >
                {cartBadgeLabel}
              </button>
            </div>
          </div>

          {filtersExpanded && (
            <>
              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setFilterMode(FILTER_MODES.ALL)}
                  className={`${filterButtonBase} ${
                    filterMode === FILTER_MODES.ALL
                      ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
                      : 'border-slate-800 bg-slate-900 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setFilterMode(FILTER_MODES.POPULAR)}
                  className={`${filterButtonBase} ${
                    filterMode === FILTER_MODES.POPULAR
                      ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
                      : 'border-slate-800 bg-slate-900 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  Popular
                </button>
              </div>
              <div className="flex flex-wrap gap-2 overflow-x-auto py-2">
                <button
                  type="button"
                  onClick={() => setSelectedCategory('all')}
                  className={`rounded-full border px-4 py-1 text-[11px] font-semibold tracking-[0.3em] transition ${
                    selectedCategory === 'all'
                      ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
                      : 'border-slate-800 bg-slate-900 text-slate-400 hover:border-emerald-400'
                  }`}
                >
                  All products
                </button>
                {categoryList.map((category) => (
                  <button
                    key={category.name}
                    type="button"
                    onClick={() => setSelectedCategory(category.name)}
                    className={`rounded-full border px-4 py-1 text-[11px] font-semibold tracking-[0.3em] transition ${
                      selectedCategory.toLowerCase() === category.name.toLowerCase()
                        ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
                        : 'border-slate-800 bg-slate-900 text-slate-400 hover:border-emerald-400'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          {loading ? (
            <div className="col-span-2 flex items-center justify-center rounded-[30px] border border-slate-800/70 bg-slate-900/60 p-10 text-sm text-slate-400">
              Loading preorder menu…
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="col-span-2 rounded-[30px] border border-slate-800/70 bg-slate-900/60 p-10 text-center text-sm text-slate-400">
              No preorder items are available right now. Check back later.
            </div>
          ) : (
            filteredProducts.map((product) => (
              <Card
                key={product.id}
                product={product}
                currencyCode={currencyCode}
                onAdd={() => handleAddToCart(product)}
              />
            ))
          )}
        </section>
      </main>

      <div className="fixed left-6 bottom-6 z-30 hidden rounded-full border border-emerald-500/40 bg-slate-900/80 p-1 shadow-[0_15px_40px_-20px_rgba(0,0,0,0.8)] sm:flex">
        <button
          type="button"
          onClick={openCart}
          className="rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 px-6 py-2 text-sm font-semibold uppercase tracking-[0.3em] text-slate-950 shadow-lg"
        >
          Cart · {cartSummaryLabel}
        </button>
      </div>

      <button
        type="button"
        onClick={openCart}
        className="fixed inset-x-6 bottom-4 z-30 flex items-center justify-center rounded-2xl border border-emerald-500/50 bg-gradient-to-r from-emerald-500/80 to-emerald-400/80 px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-slate-900 shadow-xl transition hover:from-emerald-400 hover:to-emerald-300 sm:hidden"
      >
        {cartBadgeLabel}
      </button>

      <div
        className={`fixed inset-0 z-50 ${isDrawerVisible ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!isDrawerVisible}
      >
        <div
          className={`absolute inset-0 bg-black/60 transition-opacity ${
            isDrawerVisible ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeDrawer}
        />
        <div
          className={`${drawerPanelBase} ${isCartMode ? 'translate-x-0' : 'translate-x-full'}`}
          aria-hidden={!isCartMode}
        >
          {isCartMode && (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{visibleLabel}</p>
                  <h2 className="text-xl font-semibold">Your cart · {cartSummaryLabel}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeDrawer}
                    className="rounded-full border border-slate-700/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 transition hover:border-slate-500"
                  >
                    Hide
                  </button>
                  <button
                    type="button"
                    onClick={clearCart}
                    className="rounded-full border border-rose-500 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-rose-200 transition hover:border-rose-400"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto pr-1">
                {cart.length === 0 ? (
                  <p className="text-sm text-slate-400">Cart is empty. Tap any product to add it.</p>
                ) : (
                  <div className="mt-4 space-y-3">
                    {cart.map((item) => (
                      <CartItem
                        key={item.productId}
                        item={item}
                        currencyCode={currencyCode}
                        onChange={handleQuantityChange}
                        onRemove={() => handleRemoveItem(item.productId)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="border-y border-slate-800/60 px-1 py-2 text-base text-slate-300">
                <div className="flex items-center justify-between">
                  <span className="text-sm uppercase tracking-[0.3em] text-slate-500">Subtotal</span>
                  <span className="text-xl font-semibold text-white">
                    {formatCurrency(cartTotal, currencyCode)}
                  </span>
                </div>
              </div>

              {cart.length > 0 && (
                <button
                  type="button"
                  onClick={openCheckout}
                  className="mt-2 w-full rounded-2xl border border-emerald-500 px-5 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-emerald-200 transition hover:border-emerald-400"
                >
                  Checkout
                </button>
              )}
            </div>
          )}
        </div>
        <div
          className={`${drawerPanelBase} ${isCheckoutMode ? 'translate-x-0' : 'translate-x-full'}`}
          aria-hidden={!isCheckoutMode}
        >
          {isCheckoutMode && (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Checkout</p>
                  <h2 className="text-xl font-semibold">Fill in your details</h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={backToCart}
                    className="rounded-full border border-slate-700/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 transition hover:border-slate-500"
                  >
                    Back to cart
                  </button>
                  <button
                    type="button"
                    onClick={closeDrawer}
                    className="rounded-full border border-slate-700/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 transition hover:border-slate-500"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="flex flex-1 flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto pr-1 pb-4">
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <ToggleOption
                      label="Delivery"
                      selected={deliveryOption === 'delivery'}
                      onChange={() => setDeliveryOption('delivery')}
                    />
                    <ToggleOption
                      label="Collection"
                      selected={deliveryOption === 'collection'}
                      onChange={() => setDeliveryOption('collection')}
                    />
                  </div>

                  <div className="mt-4 space-y-3">
                    <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Notes</label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Any special requests or missing products?"
                      className="w-full rounded-2xl border border-slate-800 bg-slate-950/80 px-4 py-3 text-sm text-white focus:border-emerald-400 focus:outline-none"
                    />
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setRememberDetails((prev) => !prev)}
                      aria-pressed={rememberDetails}
                      className={`flex items-center gap-2 rounded-2xl border px-4 py-2 text-xs font-semibold tracking-[0.3em] transition ${
                        rememberDetails
                          ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
                          : 'border-slate-800 bg-slate-900 text-slate-400 hover:border-slate-600'
                      }`}
                    >
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${
                          rememberDetails ? 'bg-emerald-400' : 'bg-slate-600'
                        }`}
                      />
                      Remember my details
                    </button>
                    <p className="text-[11px] text-slate-400">
                      Save your name, WhatsApp, and delivery info for next time.
                    </p>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <LabelAndInput
                      label="Name"
                      value={contact.name}
                      onChange={(value) => setContact((prev) => ({ ...prev, name: value }))}
                      required
                    />
                <PhoneInput
                  label="WhatsApp Number"
                  value={contact.whatsapp}
                  onChange={(value) => setContact((prev) => ({ ...prev, whatsapp: value }))}
                  required
                  placeholder="WhatsApp number"
                  showLabel={false}
                />
                    <LabelAndInput
                      label="Email (optional)"
                      type="email"
                      value={contact.email}
                      onChange={(value) => setContact((prev) => ({ ...prev, email: value }))}
                    />

                    {deliveryOption === 'delivery' && (
                      <LabelAndInput
                        label="Address (delivery)"
                        value={address}
                        onChange={(value) => setAddress(value)}
                        required
                      />
                    )}
                  </div>
                </div>
                <div className="border-y border-slate-800/60 px-1 py-2 text-base text-slate-300">
                  <div className="flex items-center justify-between">
                    <span className="text-sm uppercase tracking-[0.3em] text-slate-500">Subtotal</span>
                    <span className="text-xl font-semibold text-white">
                      {formatCurrency(cartTotal, currencyCode)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="mt-2 w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-400 px-5 py-3 text-sm font-semibold text-slate-900 transition hover:from-emerald-400 hover:to-emerald-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? 'Submitting...' : 'Submit pre-order'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ product, currencyCode, onAdd }) {
  const stockMessage =
    product.stock !== null && product.stock < 50
      ? `Only ${product.stock} left`
      : 'In stock';
  const price = formatCurrency(product.priceInclTax ?? product.price ?? 0, currencyCode);

  return (
    <button
      type="button"
      onClick={onAdd}
      className="group flex h-full flex-col justify-between rounded-3xl border border-slate-800/70 bg-gradient-to-br from-slate-950/90 to-slate-900/80 p-4 text-left transition hover:border-emerald-400/60"
    >
      <div>
        <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-[0.4em] text-slate-500">
          <span>{product.category || 'General'}</span>
          <span className="text-emerald-300">{price}</span>
        </div>
        <h3 className="text-lg font-semibold text-white">{product.name}</h3>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.4em] text-slate-500">{stockMessage}</p>
        <span className="rounded-full border border-emerald-500 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-emerald-200 transition group-hover:border-emerald-300">
          Add
        </span>
      </div>
    </button>
  );
}

function CartItem({ item, currencyCode, onChange, onRemove }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-4">
      <p className="text-sm font-semibold text-white">{item.name}</p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onChange(item.productId, item.quantity - 1)}
            className="h-8 w-8 rounded-full border border-slate-700 text-slate-200 transition hover:border-emerald-400"
          >
            –
          </button>
          <span className="w-6 text-center font-semibold text-white">{item.quantity}</span>
          <button
            type="button"
            onClick={() => onChange(item.productId, item.quantity + 1)}
            className="h-8 w-8 rounded-full border border-slate-700 text-slate-200 transition hover:border-emerald-400"
          >
            +
          </button>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-[11px] uppercase tracking-[0.3em]">{formatCurrency(item.price, currencyCode)} each</span>
          <button
            type="button"
            onClick={onRemove}
            className="text-xs font-semibold uppercase tracking-[0.3em] text-rose-400 transition hover:text-rose-300"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function ToggleOption({ label, selected, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`rounded-2xl border px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] transition ${
        selected
          ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
          : 'border-slate-800 bg-slate-950 text-slate-400 hover:border-emerald-400'
      }`}
    >
      {label}
    </button>
  );
}

function LabelAndInput({ label, value, onChange, type = 'text', required }) {
  return (
    <label className="flex flex-col gap-1 text-sm text-slate-300">
      <span className="text-[11px] uppercase tracking-[0.3em] text-slate-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        className="rounded-2xl border border-slate-800 bg-slate-950/80 px-4 py-3 text-sm text-white focus:border-emerald-400 focus:outline-none"
      />
    </label>
  );
}
